import { useEffect, useRef } from "react";
import { toast } from "sonner";
import { Facebook, Instagram, Link2, X as Close } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "./ui/dialog";
import { BACKEND, fileUrl } from "../lib/api";
import { useLang } from "../lib/i18n";
import { formatPrice } from "../lib/format";

const Svg = ({ d }) => <svg viewBox="0 0 24 24" className="h-6 w-6" fill="currentColor" aria-hidden="true"><path d={d} /></svg>;
const WA = "M12 2a10 10 0 0 0-8.6 15.1L2 22l5-1.3A10 10 0 1 0 12 2Zm5.3 14.1c-.2.6-1.3 1.2-1.8 1.3-.5 0-1 .3-3.4-.7-2.9-1.2-4.7-4.1-4.8-4.3-.1-.2-1.2-1.6-1.2-3s.8-2.2 1-2.5c.3-.3.6-.4.8-.4h.6c.2 0 .4 0 .6.5l.9 2.1c.1.2.1.4 0 .6l-.4.6-.4.5c-.1.1-.3.3-.1.6.2.3.8 1.3 1.7 2.1 1.1 1 2.1 1.3 2.4 1.5.3.1.5.1.6-.1l.9-1.1c.2-.3.4-.2.7-.1l2 1c.3.1.5.2.5.3.1.2.1.7-.1 1.3Z";
const TT = "M16.6 2h-3.4v13.4a2.9 2.9 0 1 1-2.9-2.9c.3 0 .6 0 .9.1V9.1a6.4 6.4 0 1 0 5.4 6.3V8.6a8 8 0 0 0 4.4 1.4V6.6A4.4 4.4 0 0 1 16.6 2Z";

const shareUrlOf = (listing) => `${BACKEND}/api/share/l/${listing.id}`;

function useShareFile(listing, open) {
  const ref = useRef(null);
  useEffect(() => {
    ref.current = null;
    const photo = listing?.photos?.[0];
    if (!open || !photo || !navigator.canShare) return;
    fetch(fileUrl(photo)).then((r) => r.blob()).then((b) => {
      const f = new File([b], "mercat-menorca.jpg", { type: b.type || "image/jpeg" });
      if (navigator.canShare({ files: [f] })) ref.current = f;
    }).catch(() => {});
  }, [listing?.id, open]); // eslint-disable-line react-hooks/exhaustive-deps
  return ref;
}

export function useShare(listing, open) {
  const { t } = useLang();
  const fileRef = useShareFile(listing, open);
  const url = listing ? shareUrlOf(listing) : "";
  const text = listing ? `${listing.title} · Mercat Menorca` : "";

  const copy = async (msg = t("share.copied")) => {
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      const el = Object.assign(document.createElement("textarea"), { value: url });
      document.body.appendChild(el);
      el.select();
      document.execCommand("copy");
      el.remove();
    }
    toast.success(msg);
  };

  const native = async (withFile = true) => {
    if (!navigator.share) return false;
    const data = { title: text, text, url };
    if (withFile && fileRef.current) data.files = [fileRef.current];
    try {
      await navigator.share(data);
      return true;
    } catch (e) {
      if (e.name === "AbortError") return true;
      if (data.files) return native(false);
      return false;
    }
  };

  const toApp = async (scheme, web) => {
    if (await native(true)) return;
    await copy(t("share.copiedSocial"));
    window.location.href = scheme;
    setTimeout(() => !document.hidden && window.open(web, "_blank", "noopener"), 1200);
  };

  const enc = encodeURIComponent;
  const links = {
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${enc(url)}`,
    whatsapp: `https://wa.me/?text=${enc(`${listing?.title || ""} ${url}`)}`,
  };
  return { url, links, copy, toApp };
}

const NETWORKS = [
  { key: "facebook", label: "Facebook", bg: "#1877F2", icon: <Facebook className="h-6 w-6" fill="currentColor" strokeWidth={0} /> },
  { key: "instagram", label: "Instagram", bg: "linear-gradient(45deg,#F58529,#DD2A7B 50%,#8134AF)", icon: <Instagram className="h-6 w-6" />, app: ["instagram://camera", "https://www.instagram.com/"] },
  { key: "tiktok", label: "TikTok", bg: "#010101", icon: <Svg d={TT} />, app: ["tiktok://", "https://www.tiktok.com/upload"] },
  { key: "whatsapp", label: "WhatsApp", bg: "#25D366", icon: <Svg d={WA} /> },
];

export function ShareSheet({ open, onOpenChange, listing, published = false, onView, onLater }) {
  const { t, lang } = useLang();
  const { links, copy, toApp } = useShare(listing, open);
  if (!listing) return null;

  const tap = (n) => (n.app ? toApp(...n.app) : window.open(links[n.key], "_blank", "noopener"));
  const photo = listing.photos?.[0];
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92dvh] w-[calc(100%-1.5rem)] max-w-md overflow-y-auto rounded-3xl p-5 sm:p-6 [&>button]:hidden" data-testid="share-sheet">
        <div className="flex items-start justify-between gap-3">
          <div>
            <DialogTitle className="font-serif text-2xl font-extrabold text-navy" data-testid="share-title">
              {t(published ? "share.publishedTitle" : "share.title")}
            </DialogTitle>
            <DialogDescription className="mt-1 text-sm text-slate-500">{t(published ? "share.publishedSub" : "share.sub")}</DialogDescription>
          </div>
          <button onClick={() => (onLater || (() => onOpenChange(false)))()} className="rounded-full p-1.5 text-slate-400 hover:bg-mist" aria-label="close" data-testid="share-close-btn">
            <Close className="h-5 w-5" />
          </button>
        </div>

        <div className="mt-4 flex items-center gap-3 rounded-2xl bg-mist p-2.5" data-testid="share-preview">
          {photo && <img src={fileUrl(photo)} alt="" className="h-16 w-16 shrink-0 rounded-xl object-cover" />}
          <div className="min-w-0">
            <p className="truncate font-bold text-navy" data-testid="share-preview-title">{listing.title}</p>
            <p className="text-sm font-extrabold text-alert" data-testid="share-preview-price">{formatPrice(listing, t, lang)}</p>
          </div>
        </div>

        <div className="mt-5 grid grid-cols-4 gap-2">
          {NETWORKS.map((n) => (
            <button key={n.key} onClick={() => tap(n)} className="group flex flex-col items-center gap-1.5" data-testid={`share-${n.key}-btn`}>
              <span className="flex h-14 w-14 items-center justify-center rounded-full text-white shadow-md transition-transform duration-200 group-hover:scale-105 group-active:scale-90"
                style={{ background: n.bg }}>{n.icon}</span>
              <span className="text-[11px] font-semibold text-navy">{n.label}</span>
            </button>
          ))}
        </div>
        <div className="mt-4 flex justify-center">
          <button onClick={() => copy()} className="flex items-center gap-1.5 rounded-full bg-mist px-4 py-2 text-sm font-bold text-navy transition-colors hover:bg-slate-200" data-testid="share-copy-btn">
            <Link2 className="h-4 w-4" /> {t("share.copy")}
          </button>
        </div>

        {published && (
          <div className="mt-6 flex gap-2.5">
            <button onClick={onLater} className="btn-outline flex-1 py-3" data-testid="share-later-btn">{t("share.later")}</button>
            <button onClick={onView} className="btn-primary flex-1 py-3" data-testid="share-view-btn">{t("share.view")}</button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
